import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { SchoolIcon, GlobeIcon, HeartIcon, ShieldIcon, CheckCircle2Icon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Container } from '../../components/ui/Container';
import { Button } from '../../components/ui/Button';
import { SubpageHero } from '../../components/sections/subpages/SubpageHero';
import { PatternDecor } from '../../components/ui/PatternDecor';
const coverage = [{
  icon: SchoolIcon,
  title: 'Navigating schools & institutions',
  body: 'How to engage teachers, principals, and administrators calmly, clearly, and effectively.',
  bg: 'bg-sage-100'
}, {
  icon: GlobeIcon,
  title: 'Understanding cultural differences',
  body: 'Recognizing where misunderstandings arise — and how to bridge them without compromising values.',
  bg: 'bg-[#EDF2F7]'
}, {
  icon: HeartIcon,
  title: 'Identity questions & emotions',
  body: 'Supporting children through difficult questions about belonging, faith, and difference.',
  bg: 'bg-[#F7EDE9]'
}, {
  icon: ShieldIcon,
  title: 'Resilience & confidence',
  body: 'Building emotional strength in children and parents — guiding without fear, protecting without isolating.',
  bg: 'bg-sand-100'
}];
export function ParentingInNonMuslimCountries() {
  return <>
      <SubpageHero eyebrow="Understanding the Environment Your Child Grows In" title="Parenting in Non-Muslim Countries" paragraphs={['Raising Muslim children in non-Muslim countries requires awareness, preparation, and emotional strength.', 'This program helps you navigate schools and institutions, understand cultural differences, support children through identity questions, and build resilience and confidence — guiding without fear and protecting without isolating.']} primaryCta={{
      label: 'Explore the Program',
      to: '#content'
    }} secondaryCta={{
      label: 'Parent Membership',
      to: '/en/parents/membership'
    }} image="https://cdn.magicpatterns.com/uploads/6d6KCRmKhV3Z3mUysSobLV/Parenting.jpg" imageAlt="Muslim family walking their child to school on a city street" compact masked data-id="element-4402" data-mp="20hemqq2jxzdn" />

      {/* What this program covers */}
      <section id="content" className="py-16 md:py-24 scroll-mt-24 bg-sage-50/60" data-id="element-4403" data-mp="14ajmn4s9qz6o">
        <Container data-id="element-4404" data-mp="1s0e3ryjmf9wq">
          <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4405" data-mp="1x11uej3xtoqf">
            What this program covers
          </h2>
          <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4406" data-mp="1j10qeusdm16q">
            Practical guidance for the real situations Muslim parents face —
            across schools, institutions, communities, and home life.
          </p>
          <div className="mt-10 grid md:grid-cols-2 gap-5" data-id="element-4407" data-mp="7oge9jn3ew5k">
            {coverage.map((c, i) => <motion.div key={c.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.06
          }} className={`rounded-2xl ${c.bg} border border-ink-900/5 p-6 flex gap-4`} data-id="element-4408" data-mp="cjdmwpggn3hx" data-mpi={i}>
                <span className="w-11 h-11 inline-flex items-center justify-center rounded-full bg-white border border-forest-700/10 text-forest-700 flex-shrink-0" data-id="element-4409" data-mp="3ci6fdyd06zb7">
                  <c.icon size={18} strokeWidth={1.6} data-id="element-4410" data-mp="3mhe0x8tw609e" />
                </span>
                <div data-id="element-4411" data-mp="2ysb9xaddav28">
                  <h3 className="font-display text-lg text-ink-900" data-id="element-4412" data-mp="2yrjsr4kiozvh">
                    {c.title}
                  </h3>
                  <p className="mt-1 text-ink-700 text-sm leading-relaxed" data-id="element-4413" data-mp="2tthz5ppgti5r">
                    {c.body}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </Container>
      </section>

      {/* Approach */}
      <section className="relative py-16 md:py-24 overflow-hidden isolate bg-white/60" data-id="element-4414" data-mp="262t2r3mktpub">
        <PatternDecor side="left" y="top" size="lg" data-id="element-4415" data-mp="2aub8s63gigm5" />
        <Container data-id="element-4416" data-mp="2avw0zp2atngu">
          <div className="grid lg:grid-cols-12 gap-12 items-center" data-id="element-4417" data-mp="2fsuv47lypuf1">
            <div className="lg:col-span-6" data-id="element-4418" data-mp="2fsmhuelxgn3b">
              <h2 className="font-display text-3xl md:text-4xl text-ink-900 leading-tight" data-id="element-4419" data-mp="2krfvc717se3h">
                Our approach
              </h2>
              <p className="mt-5 text-ink-700 leading-relaxed" data-id="element-4420" data-mp="2prk1p48ersfz">
                We help parents lead with clarity and calm — not fear. Children
                absorb the emotional state of the adults around them, and our
                work focuses on giving parents the tools to model dignity,
                confidence, and respectful engagement.
              </p>
              <ul className="mt-6 space-y-3 text-ink-700" data-id="element-4421" data-mp="rvd8jaeksyr0">
                {['Guide without fear — confidence is taught by example', 'Protect without isolating — strength comes from belonging, not withdrawal', 'Engage institutions calmly — dignity over confrontation', 'Support identity questions honestly and age-appropriately'].map(p => <li key={p} className="flex items-start gap-3" data-id="element-4422" data-mp="1at6d0h4ulgqi">
                    <CheckCircle2Icon size={18} className="text-forest-700 mt-1 flex-shrink-0" strokeWidth={1.6} data-id="element-4423" data-mp="15umme3fb7grt" />
                    <span data-id="element-4424" data-mp="wt5023bb6iuo">{p}</span>
                  </li>)}
              </ul>
            </div>
            <div className="lg:col-span-6" data-id="element-4425" data-mp="3mka0c9i3qd6a">
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-card" data-id="element-4426" data-mp="3mh5qowlvap88">
                <img src="https://cdn.magicpatterns.com/uploads/rijTYZAoAnis9wjifPVz2x/img1.jpg" alt="Muslim mother and child speaking calmly with a school administrator" className="w-full h-full object-cover" data-id="element-4427" data-mp="3hmiarm4xj359" />
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#F7EDE9]/60" data-id="element-4428" data-mp="3e0ok769thvxy">
        <Container data-id="element-4429" data-mp="30atfwllx3xqk">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8" data-id="element-4430" data-mp="30abd164r2l9o">
            <div className="max-w-xl" data-id="element-4431" data-mp="352uy0o5roioi">
              <h2 className="font-display text-3xl md:text-5xl text-ink-900 leading-tight" data-id="element-4432" data-mp="353mltct4hc48">
                Guide your family with clarity
              </h2>
              <p className="mt-4 text-ink-700" data-id="element-4433" data-mp="2vd1mto9je5ks">
                Explore parent membership to get ongoing guidance, expert input,
                and a community of Muslim parents.
              </p>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-4434" data-mp="2mashrdx1f4es">
              <Link to="/en/waitlist" data-id="element-4435" data-mp="22pa73qaz1cbe">
                <Button variant="primary" data-id="element-4436" data-mp="22qbjby3q44up">Join Membership</Button>
              </Link>
              <Link to="/en/parents/preventing-discrimination" data-id="element-4437" data-mp="1xrgykqm7467y">
                <Button variant="secondary" data-id="element-4438" data-mp="2bpyjkgkpv4m6">Preventing Discrimination</Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </>;
}